import { convertTimeZone, todayWithSystemTimeZone } from '@magicdoorinc/date';
import { useLocalization, cn } from '@magicdoorinc/ui';
import { createMemo, Match, Switch } from 'solid-js';
import { isMonthToMonth, isDraftLease, isEndedLease, isEvictionLease, isExpiredLease, isFutureLease } from '~/components/leases/utils';
import { LeaseStatus as ILeaseStatus } from '~/swagger/portal/Api';
import { IconActive, IconPastDue, IconInEviction, IconEndingSoon, IconEnded } from './LeaseIcons';
import type { Component } from 'solid-js';
import type { HydratedLeaseDto, LeaseDto } from '~/swagger/portal/Api';

type LeaseStatusProps = {
  class?: string;
  lease?: HydratedLeaseDto | LeaseDto;
};

export const LeaseStatus: Component<LeaseStatusProps> = (props) => {
  const { t } = useLocalization();

  return (
    <span class={cn('text-sm font-medium', props.class)}>
      <Switch fallback={<span class="text-success">{t('Leased')}</span>}>
        <Match when={isEvictionLease(props.lease)}>
          <span class="text-danger-active">{t('Eviction')}</span>
        </Match>
        <Match when={isMonthToMonth(props.lease)}>
          <span class="text-dark-navy">{t('Month to month')}</span>
        </Match>
        <Match when={isExpiredLease(props.lease)}>
          <span class="text-dragonFruit">{t('Expired')}</span>
        </Match>

        <Match when={isEndedLease(props.lease)}>
          <span class="text-warning">{t('Ended')}</span>
        </Match>
        <Match when={isFutureLease(props.lease)}>
          <span class="text-link">{t('Coming up')}</span>
        </Match>
        <Match when={isDraftLease(props.lease)}>
          <span class="text-text-02">{t('Draft')}</span>
        </Match>
        <Match when={!props.lease}>
          <span class="text-danger">{t('Not leased')}</span>
        </Match>
      </Switch>
    </span>
  );
};

type LeaseStatusElementProps = {
  class?: string;
  lease: HydratedLeaseDto | LeaseDto;
  isPill?: boolean;
};

export const LeaseStatusElement: Component<LeaseStatusElementProps> = (props) => {
  const { t } = useLocalization();

  const status = createMemo(() => {
    if (props.lease.eviction) return 'eviction';
    if (props.lease.ended) return 'ended';
    if (props.lease.balance > 0 && props.lease.pastDueDays) return 'due';
    if (
      props.lease.end &&
      props.lease.status !== ILeaseStatus.MonthToMonth &&
      convertTimeZone(props.lease.end).diff(todayWithSystemTimeZone(), 'day') <= 30
    )
      return 'ending';
    return 'active';
  });

  const getStatusStyles = (status: string) => {
    const baseStyles = 'flex items-center gap-1 whitespace-nowrap';
    const pillStyles = props.isPill ? 'rounded-full bg-current-alpha px-2 py-0.5 text-xs' : '';

    switch (status) {
      case 'active':
        return cn(baseStyles, pillStyles, {
          'text-dragonFruit': props.lease.status === ILeaseStatus.Draft,
          'text-success': props.lease.status !== ILeaseStatus.Draft,
        });
      case 'due':
        return cn(baseStyles, pillStyles, 'text-danger');
      case 'eviction':
        return cn(baseStyles, pillStyles, 'text-text-03');
      case 'ending':
        return cn(baseStyles, pillStyles, 'text-warning');
      case 'ended':
        return cn(baseStyles, pillStyles, 'text-text-03');
      default:
        return baseStyles;
    }
  };

  return (
    <div class={cn('w-fit text-sm', props.class)}>
      <Switch>
        <Match when={status() === 'active'}>
          <div class={getStatusStyles('active')}>
            <IconActive class="size-4" />
            {t(props.lease.status === ILeaseStatus.Draft ? 'Draft' : 'Active')}
          </div>
        </Match>
        <Match when={status() === 'due'}>
          <div classList={{ 'flex items-center gap-1': props.isPill }}>
            <div class={getStatusStyles('due')}>
              <IconPastDue class="size-4" />
              {t('Past due')}
            </div>
            <span class="mr-1 whitespace-nowrap text-xs text-text-03">{t('Past due')}</span>
            <span class="whitespace-nowrap text-xs text-danger">
              {props.lease.pastDueDays && t('{count} Days', { count: props.lease.pastDueDays.toString() })}
            </span>
          </div>
        </Match>
        <Match when={status() === 'eviction'}>
          <div class={getStatusStyles('eviction')}>
            <IconInEviction class="size-4" />
            {t('In eviction')}
          </div>
        </Match>
        <Match when={status() === 'ending'}>
          <div classList={{ 'flex items-center gap-1': props.isPill }}>
            <div class={getStatusStyles('ending')}>
              <IconEndingSoon class="size-4" />
              {t('Ending soon')}
            </div>
            <span class="text-xs text-text-03">
              {t('Due in {count} days', { count: convertTimeZone(props.lease.end).diff(todayWithSystemTimeZone(), 'day') })}
            </span>
          </div>
        </Match>
        <Match when={status() === 'ended'}>
          <div class={getStatusStyles('ended')}>
            <IconEnded class="size-4" />
            {t('Ended lease')}
          </div>
        </Match>
      </Switch>
    </div>
  );
};
